<template>
  <div class="bg-[#f6f1ea] text-[#1a1714]">
    <section class="relative isolate min-h-[68svh] overflow-hidden bg-[#090807]">
      <KImage
        src="/k/kendrick-paris/store/kendrick-store.jpg"
        :alt="copy.heroAlt"
        class="absolute inset-0 h-full w-full object-cover opacity-55"
        sizes="sm:100vw md:100vw lg:100vw"
        loading="eager"
        :fetchpriority="'high'"
      />
      <div class="absolute inset-0 bg-[linear-gradient(180deg,rgba(8,7,6,0.22)_0%,rgba(8,7,6,0.12)_38%,rgba(8,7,6,0.78)_100%)]"></div>
      <div class="relative mx-auto flex min-h-[68svh] max-w-[1680px] flex-col justify-end px-6 pb-14 pt-32 sm:px-8 lg:px-14 lg:pb-20">
        <p class="text-[0.62rem] uppercase tracking-[0.36em] text-[#f3ede3]/50">
          {{ copy.heroEyebrow }}
        </p>
        <h1 class="mt-5 font-['Playfair_Display'] text-[4rem] leading-[0.85] tracking-[-0.06em] text-[#fbf5eb] sm:text-[5.5rem] lg:text-[7.2rem]">
          {{ copy.title }}
        </h1>
        <p class="mt-6 max-w-[34rem] text-[0.88rem] leading-[1.72] text-[#f3ede3]/70 sm:text-[0.94rem]">
          {{ copy.heroBody }}
        </p>
      </div>
    </section>

    <div class="mx-auto max-w-[1680px] px-6 pt-6 sm:px-8 lg:px-14">
      <PageBreadcrumb :paths="breadcrumbPaths" />
    </div>

    <section class="mx-auto max-w-[1680px] px-6 py-20 sm:px-8 lg:px-14 lg:py-28">
      <div class="grid gap-16 lg:grid-cols-[0.9fr_1.1fr] lg:gap-24">
        <div>
          <p class="text-[0.62rem] uppercase tracking-[0.32em] text-[#1a1714]/40">
            {{ copy.introEyebrow }}
          </p>
          <h2 class="mt-4 max-w-[14ch] font-['Playfair_Display'] text-[2.6rem] leading-[0.9] tracking-[-0.05em] sm:text-[3.4rem]">
            {{ copy.introTitle }}
          </h2>
        </div>

        <div class="space-y-8 text-[0.95rem] leading-[1.8] text-[#1a1714]/74 lg:pt-4">
          <p>{{ copy.introBodyOne }}</p>
          <p>{{ copy.introBodyTwo }}</p>

          <div
            v-if="featuredStore"
            class="rounded-[1.6rem] border border-[#1a1714]/10 bg-white/55 p-7 shadow-[0_24px_90px_-60px_rgba(0,0,0,0.35)]"
          >
            <p class="text-[0.62rem] uppercase tracking-[0.3em] text-[#1a1714]/38">
              {{ copy.featuredLabel }}
            </p>
            <div class="mt-4 flex flex-wrap items-end justify-between gap-4">
              <div>
                <h3 class="font-['Playfair_Display'] text-[2rem] leading-[0.95] tracking-[-0.04em] text-[#1a1714]">
                  {{ featuredStore.name }}
                </h3>
                <p class="mt-3 text-[0.86rem] text-[#1a1714]/58">
                  {{ formattedAddress(featuredStore).join(' · ') }}
                </p>
              </div>
              <a
                :href="mapsUrl(featuredStore)"
                target="_blank"
                rel="noopener"
                class="inline-flex min-h-12 items-center justify-center rounded-full border border-[#1a1714]/16 px-6 py-3 text-[0.72rem] uppercase tracking-[0.28em] text-[#1a1714] transition hover:border-[#1a1714]/34"
              >
                {{ copy.openMaps }}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>

    <section class="bg-[#0c0a09] text-[#f3ede3]">
      <div class="mx-auto max-w-[1680px] px-6 py-20 sm:px-8 lg:px-14 lg:py-28">
        <div class="grid gap-16 lg:grid-cols-[0.76fr_1.24fr] lg:gap-24">
          <div>
            <p class="text-[0.62rem] uppercase tracking-[0.32em] text-[#f3ede3]/40">
              {{ copy.locationsEyebrow }}
            </p>
            <h2 class="mt-4 max-w-[15ch] font-['Playfair_Display'] text-[2.6rem] leading-[0.9] tracking-[-0.05em] sm:text-[3.4rem]">
              {{ copy.locationsTitle }}
            </h2>
          </div>

          <div v-if="physicalStores.length" class="space-y-8">
            <article
              v-for="store in physicalStores"
              :key="store.id"
              class="rounded-[1.7rem] border border-[#f3ede3]/10 bg-[#f3ede3]/[0.05] p-7 sm:p-8"
            >
              <div class="grid gap-10 lg:grid-cols-[0.9fr_1.1fr] lg:gap-14">
                <div>
                  <p class="text-[0.62rem] uppercase tracking-[0.3em] text-[#f3ede3]/38">
                    {{ copy.locationLabel }}
                  </p>
                  <h3 class="mt-4 font-['Playfair_Display'] text-[2rem] leading-[0.96] tracking-[-0.04em] text-[#fbf5eb]">
                    {{ store.name }}
                  </h3>

                  <div class="mt-6 space-y-2 text-[0.9rem] leading-[1.7] text-[#f3ede3]/72">
                    <p
                      v-for="(line, index) in formattedAddress(store)"
                      :key="`${store.id}-addr-${index}`"
                    >
                      {{ line }}
                    </p>
                  </div>

                  <div class="mt-6 space-y-2 text-[0.86rem] text-[#f3ede3]/60">
                    <p v-if="cleanText(store.phone)">
                      <a :href="phoneUrl(store.phone)" class="transition hover:text-[#fbf5eb]">{{ cleanText(store.phone) }}</a>
                    </p>
                    <p v-if="cleanText(store.email)">
                      <a :href="`mailto:${cleanText(store.email)}`" class="transition hover:text-[#fbf5eb]">{{ cleanText(store.email) }}</a>
                    </p>
                  </div>

                  <div class="mt-8 flex flex-wrap gap-4">
                    <a
                      :href="mapsUrl(store)"
                      target="_blank"
                      rel="noopener"
                      class="inline-flex min-h-12 items-center justify-center rounded-full border border-[#f3ede3]/18 px-6 py-3 text-[0.72rem] uppercase tracking-[0.28em] text-[#f3ede3] transition hover:border-[#f3ede3]/36"
                    >
                      {{ copy.openMaps }}
                    </a>
                    <NuxtLink
                      :to="contactPath"
                      class="inline-flex min-h-12 items-center justify-center rounded-full border border-[#f3ede3]/12 px-6 py-3 text-[0.72rem] uppercase tracking-[0.28em] text-[#f3ede3]/70 transition hover:border-[#f3ede3]/28 hover:text-[#f3ede3]"
                    >
                      {{ copy.contactCta }}
                    </NuxtLink>
                  </div>
                </div>

                <div>
                  <p class="text-[0.62rem] uppercase tracking-[0.3em] text-[#f3ede3]/38">
                    {{ copy.hoursLabel }}
                  </p>
                  <div class="mt-5 space-y-3">
                    <div
                      v-for="row in storeScheduleRows(store)"
                      :key="`${store.id}-${row.dayKey}`"
                      class="flex items-center justify-between gap-6 border-b border-[#f3ede3]/10 pb-3 text-[0.86rem]"
                    >
                      <span class="text-[#f3ede3]/58">{{ row.dayLabel }}</span>
                      <span class="text-right text-[#f3ede3]/82">{{ row.hoursLabel }}</span>
                    </div>
                  </div>
                </div>
              </div>
            </article>
          </div>

          <div v-else class="rounded-[1.7rem] border border-[#f3ede3]/10 bg-[#f3ede3]/[0.05] p-7 sm:p-8">
            <p class="text-[0.95rem] leading-[1.8] text-[#f3ede3]/70">
              {{ copy.noStores }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <section class="mx-auto max-w-[1680px] px-6 py-20 sm:px-8 lg:px-14 lg:py-28">
      <div class="grid gap-12 rounded-[1.8rem] border border-[#1a1714]/10 bg-white/50 p-7 sm:p-8 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:p-10">
        <div>
          <p class="text-[0.62rem] uppercase tracking-[0.32em] text-[#1a1714]/38">
            {{ copy.onlineEyebrow }}
          </p>
          <h2 class="mt-4 font-['Playfair_Display'] text-[2.3rem] leading-[0.92] tracking-[-0.05em] text-[#1a1714] sm:text-[3rem]">
            {{ copy.onlineTitle }}
          </h2>
          <p class="mt-5 max-w-[34rem] text-[0.95rem] leading-[1.8] text-[#1a1714]/68">
            {{ copy.onlineBody }}
          </p>
        </div>

        <div class="flex flex-wrap gap-4 lg:justify-end">
          <NuxtLink
            :to="homePath"
            class="inline-flex min-h-12 items-center justify-center rounded-full bg-[#1a1714] px-7 py-3 text-[0.72rem] uppercase tracking-[0.28em] text-[#f6f1ea] transition hover:bg-black"
          >
            {{ copy.discoverCta }}
          </NuxtLink>
          <NuxtLink
            :to="contactPath"
            class="inline-flex min-h-12 items-center justify-center rounded-full border border-[#1a1714]/14 px-7 py-3 text-[0.72rem] uppercase tracking-[0.28em] text-[#1a1714] transition hover:border-[#1a1714]/30"
          >
            {{ copy.contactCta }}
          </NuxtLink>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import {
  SITE_LOCALES,
  getLocaleLanguage,
  localizePath
} from '~/utils/locales'

type StoreHoursEntry = {
  closed?: boolean
  status?: string
  open?: string
  close?: string
}

type StoreRecord = {
  id: number | string
  name: string
  type: string
  address?: string | null
  address_line1?: string | null
  postal_code?: string | null
  city?: string | null
  country?: string | null
  phone?: string | null
  email?: string | null
  latitude?: number | null
  longitude?: number | null
  google_maps_url?: string | null
  hours?: Record<string, StoreHoursEntry> | string | null
}

const siteUrl = 'https://kendrick.paris'
const heroImage = `${siteUrl}/k/kendrick-paris/store/kendrick-store.jpg`

const siteLocale = useSiteLocale()
const isFrench = computed(() => siteLocale.value === 'fr')
const isJapanese = computed(() => siteLocale.value === 'ja')

const { data: storesResponse } = useFetch<StoreRecord[]>('/api/stores', {
  key: 'public-stores-page',
  lazy: import.meta.client,
  getCachedData: (key, nuxtApp) => nuxtApp.payload.data[key] ?? nuxtApp.static.data[key],
  default: () => []
})

const allStores = computed(() => Array.isArray(storesResponse.value) ? storesResponse.value : [])
const physicalStores = computed(() => allStores.value.filter(store => store.type === 'physical'))
const featuredStore = computed(() => physicalStores.value[0] || null)
const contactPath = computed(() => localizePath('/contact', siteLocale.value))
const homePath = computed(() => localizePath('/', siteLocale.value))

const copy = computed(() => ({
  contactCta: isJapanese.value ? 'お問い合わせ' : isFrench.value ? 'Contact' : 'Contact',
  discoverCta: isJapanese.value ? 'コレクションを見る' : isFrench.value ? 'Voir la collection' : 'View collection',
  featuredLabel: isJapanese.value ? '現在の住所' : isFrench.value ? 'Adresse en vue' : 'Current address',
  heroAlt: isJapanese.value ? 'Kendrick Paris ストア' : isFrench.value ? 'Boutique Kendrick Paris' : 'Kendrick Paris store',
  heroBody: isJapanese.value
    ? 'パリのKendrickスペースをご覧ください。住所、営業時間、地図への直接アクセスを掲載しています。'
    : isFrench.value
    ? 'Visiter les espaces Kendrick à Paris. Adresses, horaires et accès direct aux cartes.'
    : 'Visit the current Kendrick spaces in Paris. Addresses, opening hours, and direct map access.',
  heroEyebrow: 'Paris',
  title: isJapanese.value ? 'ストア' : 'Store',
  hoursLabel: isJapanese.value ? '営業時間' : isFrench.value ? 'Horaires' : 'Hours',
  introBodyOne: isJapanese.value
    ? 'ストアページでは、来店場所、来店時間、ルートの開き方を直接確認できます。'
    : isFrench.value
    ? 'La page store doit donner l’essentiel sans détour : où venir, quand venir, et comment ouvrir le trajet.'
    : 'The store page should give the essentials directly: where to come, when to come, and how to open the route.',
  introBodyTwo: isJapanese.value
    ? '有効な拠点はストア情報から表示されます。住所が変わると、このページも更新されます。'
    : isFrench.value
    ? 'Les lieux actifs sont tenus ici à partir du flux boutique. Quand l’adresse change, la page suit.'
    : 'Active locations are listed here from the store feed. When an address changes, the page follows.',
  introEyebrow: isJapanese.value ? 'アクセス' : isFrench.value ? 'Accès' : 'Access',
  introTitle: isJapanese.value ? 'Kendrickの拠点。' : isFrench.value ? 'Les points de présence Kendrick.' : 'Kendrick points of presence.',
  locationLabel: isJapanese.value ? '場所' : isFrench.value ? 'Lieu' : 'Location',
  locationsEyebrow: isJapanese.value ? '有効な住所' : isFrench.value ? 'Adresses actives' : 'Active addresses',
  locationsTitle: isJapanese.value ? '店舗で見る。' : isFrench.value ? 'Venir en boutique.' : 'Visit in person.',
  noStores: isJapanese.value
    ? '現在、有効な実店舗住所は表示されていません。お問い合わせページをご利用ください。'
    : isFrench.value
    ? 'Aucune adresse physique active n’est remontée pour le moment. Passe par la page contact.'
    : 'No active physical location is available in the feed right now. Use the contact page.',
  onlineBody: isJapanese.value
    ? 'サイトは常に開いています。プライベートオープン、在庫、プレオーダーへのアクセスもオンラインで案内されます。'
    : isFrench.value
    ? 'Le site reste ouvert en continu. Les ouvertures privées, disponibilités et précommandes passent aussi par le canal en ligne.'
    : 'The site stays open continuously. Private openings, availability, and preorder access also run through the online channel.',
  onlineEyebrow: isJapanese.value ? 'オンライン' : isFrench.value ? 'En ligne' : 'Online',
  onlineTitle: isJapanese.value ? 'ストアはここでも続いています。' : isFrench.value ? 'La boutique continue aussi ici.' : 'The store also continues here.',
  openMaps: isJapanese.value ? '地図を開く' : isFrench.value ? 'Ouvrir la carte' : 'Open map'
}))

const breadcrumbPaths = computed(() => [
  { url: localizePath('/store', siteLocale.value), label: isJapanese.value ? 'ストア' : 'Store' }
])

const canonicalUrl = computed(() => `${siteUrl}${localizePath('/store', siteLocale.value)}`)
const englishUrl = `${siteUrl}/store`
const metaTitle = computed(() => isJapanese.value ? 'ストア — Kendrick Paris' : 'Store — Kendrick Paris')
const metaDescription = computed(() => isJapanese.value
  ? 'パリのKendrick拠点、営業時間、地図への直接アクセス。公開ストアページは有効なブティック情報に合わせて更新されます。'
  : isFrench.value
  ? 'Adresses Kendrick à Paris, horaires et accès direct aux cartes. La page store publique suit les boutiques actives.'
  : 'Kendrick locations in Paris, opening hours, and direct map access. The public store page follows active boutiques.')

const dayOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const

const dayLabels = computed<Record<(typeof dayOrder)[number], string>>(() => ({
  monday: isJapanese.value ? '月曜日' : isFrench.value ? 'Lundi' : 'Monday',
  tuesday: isJapanese.value ? '火曜日' : isFrench.value ? 'Mardi' : 'Tuesday',
  wednesday: isJapanese.value ? '水曜日' : isFrench.value ? 'Mercredi' : 'Wednesday',
  thursday: isJapanese.value ? '木曜日' : isFrench.value ? 'Jeudi' : 'Thursday',
  friday: isJapanese.value ? '金曜日' : isFrench.value ? 'Vendredi' : 'Friday',
  saturday: isJapanese.value ? '土曜日' : isFrench.value ? 'Samedi' : 'Saturday',
  sunday: isJapanese.value ? '日曜日' : isFrench.value ? 'Dimanche' : 'Sunday'
}))

function cleanText(value?: string | null) {
  return typeof value === 'string' ? value.trim() : ''
}

function formattedAddress(store: StoreRecord) {
  const lineOne = cleanText(store.address_line1) || cleanText(store.address)
  const locality = [cleanText(store.postal_code), cleanText(store.city)].filter(Boolean).join(' ')
  const country = cleanText(store.country)
  const normalizedCountry = country && country !== cleanText(store.city) ? country : ''
  return [lineOne, locality, normalizedCountry].filter(Boolean)
}

function phoneUrl(phone?: string | null) {
  const normalized = cleanText(phone).replace(/[^+\d]/g, '')
  return `tel:${normalized}`
}

function normalizeHours(hours: StoreRecord['hours']) {
  if (!hours) return null
  if (typeof hours === 'string') {
    try {
      return JSON.parse(hours) as Record<string, StoreHoursEntry>
    } catch {
      return null
    }
  }
  return hours
}

function formatHoursLabel(entry?: StoreHoursEntry) {
  if (!entry || entry.closed || entry.status === 'closed') {
    return isJapanese.value ? '休業' : isFrench.value ? 'Fermé' : 'Closed'
  }

  if (entry.open && entry.close) {
    return `${entry.open} - ${entry.close}`
  }

  return isJapanese.value ? '要問い合わせ' : isFrench.value ? 'Sur demande' : 'On request'
}

function storeScheduleRows(store: StoreRecord) {
  const hours = normalizeHours(store.hours)
  return dayOrder.map((dayKey) => ({
    dayKey,
    dayLabel: dayLabels.value[dayKey],
    hoursLabel: formatHoursLabel(hours?.[dayKey])
  }))
}

function mapsUrl(store: StoreRecord) {
  const explicitUrl = cleanText(store.google_maps_url)
  if (explicitUrl) return explicitUrl

  const query = formattedAddress(store).join(', ') || cleanText(store.name)
  if (!query) return 'https://www.google.com/maps'
  return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(query)}`
}

const storeSchema = computed(() => physicalStores.value.map((store) => {
  const address = formattedAddress(store)

  return {
    '@context': 'https://schema.org',
    '@type': 'ShoeStore',
    name: store.name,
    url: canonicalUrl.value,
    image: heroImage,
    telephone: cleanText(store.phone) || undefined,
    email: cleanText(store.email) || undefined,
    address: {
      '@type': 'PostalAddress',
      streetAddress: address[0] || undefined,
      postalCode: cleanText(store.postal_code) || undefined,
      addressLocality: cleanText(store.city) || undefined,
      addressCountry: cleanText(store.country) || undefined
    },
    geo: store.latitude && store.longitude
      ? {
          '@type': 'GeoCoordinates',
          latitude: store.latitude,
          longitude: store.longitude
        }
      : undefined,
    sameAs: mapsUrl(store)
  }
}))

useHead({
  htmlAttrs: computed(() => ({ lang: getLocaleLanguage(siteLocale.value) })),
  title: computed(() => metaTitle.value),
  meta: computed(() => [
    { name: 'description', content: metaDescription.value },
    { name: 'robots', content: 'index, follow' },
    { property: 'og:title', content: metaTitle.value },
    { property: 'og:description', content: metaDescription.value },
    { property: 'og:type', content: 'website' },
    { property: 'og:url', content: canonicalUrl.value },
    { property: 'og:image', content: heroImage },
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: metaTitle.value },
    { name: 'twitter:description', content: metaDescription.value },
    { name: 'twitter:image', content: heroImage }
  ]),
  link: computed(() => [
    { rel: 'canonical', href: canonicalUrl.value },
    ...SITE_LOCALES.map(locale => ({
      rel: 'alternate',
      hreflang: locale.language,
      href: `${siteUrl}${localizePath('/store', locale.code)}`
    })),
    { rel: 'alternate', hreflang: 'x-default', href: englishUrl }
  ]),
  script: computed(() => [
    {
      type: 'application/ld+json',
      innerHTML: JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'WebPage',
        name: metaTitle.value,
        description: metaDescription.value,
        inLanguage: getLocaleLanguage(siteLocale.value),
        url: canonicalUrl.value
      })
    },
    ...storeSchema.value.map((entry) => ({
      type: 'application/ld+json',
      innerHTML: JSON.stringify(entry)
    }))
  ])
})
</script>
